"use client";

import { createContext, useCallback, useContext, useMemo, useState, useSyncExternalStore } from "react";

/**
 * The key the try-it panels send, shared by every panel on the page so it is
 * pasted once. It lives in React state; only the visitor's explicit
 * "remember on this device" puts it in localStorage (and unticking removes
 * it). Every storage access is guarded: a private window or blocked storage
 * just means nothing is remembered.
 */

export const API_KEY_STORAGE_KEY = "steampeek:api-docs:key";

const listeners = new Set<() => void>();

function readStored(): string | null {
  try {
    return window.localStorage.getItem(API_KEY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string | null): void {
  try {
    if (value) window.localStorage.setItem(API_KEY_STORAGE_KEY, value);
    else window.localStorage.removeItem(API_KEY_STORAGE_KEY);
  } catch {
    // Storage unavailable: the key stays in memory only.
  }
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === API_KEY_STORAGE_KEY || e.key === null) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

const serverSnapshot = () => null;

type ApiKeyState = {
  apiKey: string;
  setApiKey: (value: string) => void;
  remember: boolean;
  setRemember: (on: boolean) => void;
};

const ApiKeyContext = createContext<ApiKeyState | null>(null);

export function ApiKeyProvider({ children }: { children: React.ReactNode }) {
  // null on the server and during hydration, so the markup always matches.
  const stored = useSyncExternalStore(subscribe, readStored, serverSnapshot);
  const [typed, setTyped] = useState<string | null>(null);
  const [rememberChoice, setRememberChoice] = useState<boolean | null>(null);

  const remember = rememberChoice ?? stored !== null;
  const apiKey = typed ?? stored ?? "";

  const setApiKey = useCallback(
    (value: string) => {
      setTyped(value);
      if (remember) writeStored(value.trim() || null);
    },
    [remember],
  );

  const setRemember = useCallback(
    (on: boolean) => {
      setRememberChoice(on);
      // Keep what the field shows when the stored copy goes away under it.
      setTyped(apiKey);
      writeStored(on ? apiKey.trim() || null : null);
    },
    [apiKey],
  );

  const value = useMemo(() => ({ apiKey, setApiKey, remember, setRemember }), [apiKey, setApiKey, remember, setRemember]);
  return <ApiKeyContext.Provider value={value}>{children}</ApiKeyContext.Provider>;
}

export function useApiKey(): ApiKeyState {
  const state = useContext(ApiKeyContext);
  if (!state) throw new Error("useApiKey() needs an <ApiKeyProvider> above it");
  return state;
}
